import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useEffect, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import { objectiveActionsSchema, ObjectiveActionsValidationSchema } from "../../schemas/objectiveActions.schema";
import useUpdateObjectiveAction from "../../hooks/useUpdateMaintenanceObjectiveAction";
import { useParams } from "react-router-dom";
import { ChevronsRightIcon } from "lucide-react";
import useMaintenanceObjectiveAction from "../../hooks/useMaintenanceObjectiveAction";
import TextArea from "@/common/components/TextArea";

const MaintenanceObjectiveActionsUpdate = () => {
  const { id } = useParams();

  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const {
    data: objectiveAction,
    isPending: isObjectiveActionPending,
    error: objectiveActionError = {} as any,
    refetch: refetchObjectiveAction,
  } = useMaintenanceObjectiveAction(id);

  const {
    mutate: updateObjectiveAction,
    isPending: isUpdateObjectiveActionPending,
  } = useUpdateObjectiveAction({
    id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceObjectiveAction', id] });
      goBack({ fallback: `/objective-maintenance/actions/${id}` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<ObjectiveActionsValidationSchema>({
    resolver: zodResolver(objectiveActionsSchema),
    defaultValues: {
      display_name: objectiveAction?.display_name,
      description: objectiveAction?.description,
    },
  });

  const onSubmit: SubmitHandler<ObjectiveActionsValidationSchema> = (data) => updateObjectiveAction(data);

  useEffect(() => {
    if (!isObjectiveActionPending) {
      reset({
        display_name: objectiveAction?.display_name,
        description: objectiveAction?.description,
      });
    }
  }, [isObjectiveActionPending]);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={
        hasUserPermission('Admin::Maintenance::ObjectiveActions::Update') || isUserPermissionsPending
          ? objectiveActionError?.status
          : 403
      }
      refetchFunction={refetchObjectiveAction}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/objective-maintenance/actions/${id}` }}
          isLoading={isObjectiveActionPending}
          titleClassName="text-nowrap max-w-56 overflow-hidden text-ellipsis font-normal text-base"
          title="Objetos de manutenção"
          subtitle={(
            <div className="flex items-center gap-2">
              <h2 className="text-white-400">
                Ação
              </h2>

              <ChevronsRightIcon className="text-white-400" />

              <h2 className="text-white-800">
                {objectiveAction?.display_name}
              </h2>
            </div>
          )}
          buttons={(
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                onClick={() => goBack({ fallback: `/objective-maintenance/actions/${id}` })}
                disabled={isUpdateObjectiveActionPending}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                name="submit"
                isLoading={isUpdateObjectiveActionPending}
              >
                Confirmar
              </Button>
            </div>
          )}
        />

        <Box className="max-h-[calc(100vh-132px)] overflow-y-auto flex flex-col gap-4">
          <p className="text-lg font-medium">
            Dados gerais da ação
          </p>

          <TextField
            label="Nome da ação"
            maxLength={32}
            {...register('display_name')}
            disabled={isUpdateObjectiveActionPending}
            errorMessage={errors.display_name?.message || undefined}
            markAsRequired
          />

          <TextArea
            label="Descrição"
            {...register('description')}
            disabled={isUpdateObjectiveActionPending}
            errorMessage={errors.description?.message || undefined}
            markAsRequired
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </ErrorMessageWrapper>
  );
}

export default MaintenanceObjectiveActionsUpdate;
